import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Bot, MessageSquare, Send, X, User2, RefreshCw, Copy, Settings2, Mail, MessageCircle, CheckCheck, Lock, Link2, Search, Clock, AlertTriangle, ChevronDown, ChevronUp, RotateCcw, Calendar } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, Btn, Badge, dt } from "../components/common";
import { Modal, Field, TextArea, Selecter } from "./crm/crmui";

const ESTADO_TABS = [
  { value: "escalada", label: "Escaladas" },
  { value: "aberta", label: "Abertas (AI)" },
  { value: "fechada", label: "Fechadas" },
  { value: "todas", label: "Todas" },
];
const CANAL_TABS = [
  { value: "chat", label: "Chat AI", icon: Bot },
  { value: "email", label: "Email", icon: Mail },
  { value: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  { value: "agendadas", label: "Agendadas", icon: Clock },
];
const PROVIDERS = ["gemini", "openai", "anthropic"];

export default function ChatInbox() {
  const [searchParams, setSearchParams] = useSearchParams();
  const deepSid = searchParams.get("session") || "";
  const fromHandoff = searchParams.get("from") === "handoff";
  const [canal, setCanal] = useState(() => {
    const c = searchParams.get("canal");
    return ["email", "whatsapp", "agendadas"].includes(c) ? c : "chat";
  });
  const [tab, setTab] = useState(deepSid ? "todas" : "escalada");
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [texto, setTexto] = useState("");
  const [busy, setBusy] = useState(false);
  const [showCfg, setShowCfg] = useState(false);
  const [cfg, setCfg] = useState(null);
  const msgsEnd = useRef(null);

  // Email state
  const [emailThreads, setEmailThreads] = useState([]);
  const [emailSel, setEmailSel] = useState(null);
  const [emailMsgs, setEmailMsgs] = useState([]);
  const loadEmail = async () => {
    try { const { data } = await api.get("/email/threads"); setEmailThreads(data || []); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openEmailThread = async (t) => {
    setEmailSel(t);
    try { const { data } = await api.get(`/email/threads/${encodeURIComponent(t.email)}/messages`); setEmailMsgs(data.messages || []); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { if (canal === "email") loadEmail(); }, [canal]); // eslint-disable-line

  // WhatsApp state
  const [waConvs, setWaConvs] = useState([]);
  const [waSel, setWaSel] = useState(null);
  const [waMsgs, setWaMsgs] = useState([]);
  const [waTexto, setWaTexto] = useState("");
  const [waBusy, setWaBusy] = useState(false);
  const [waEstado, setWaEstado] = useState(""); // "" = todas | "aberta" | "fechada"
  const waMsgsEnd = useRef(null);

  const loadWaConversations = useCallback(async () => {
    try {
      const params = waEstado ? { estado: waEstado } : {};
      const { data } = await api.get("/channels/letscall/whatsapp/conversations", { params });
      setWaConvs(data || []);
    } catch { /* silencioso */ }
  }, [waEstado]);

  const openWaConversation = async (conv) => {
    setWaSel(conv);
    setWaTexto("");
    try {
      const { data } = await api.get(`/channels/letscall/whatsapp/conversations/${conv.id}/messages`);
      setWaMsgs(data.messages || []);
      // Marcar como lido
      if (conv.unread_count > 0) {
        await api.patch(`/channels/letscall/whatsapp/conversations/${conv.id}`, { unread_count: 0 });
        setWaConvs((prev) => prev.map((c) => c.id === conv.id ? { ...c, unread_count: 0 } : c));
      }
      setTimeout(() => waMsgsEnd.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const sendWaReply = async () => {
    if (!waTexto.trim() || !waSel) return;
    setWaBusy(true);
    try {
      const { data } = await api.post(`/channels/letscall/whatsapp/conversations/${waSel.id}/messages`, { body: waTexto.trim() });
      setWaMsgs((prev) => [...prev, data]);
      setWaTexto("");
      setWaConvs((prev) => prev.map((c) => c.id === waSel.id
        ? { ...c, last_message_body: waTexto.trim(), last_message_at: new Date().toISOString() }
        : c));
      setTimeout(() => waMsgsEnd.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e) {
      const msg = e.response?.data?.detail || "";
      if (e.response?.status === 422 || msg.includes("24h")) {
        toast.error("Janela de 24h expirada. O cliente precisa de iniciar a conversa primeiro.", { duration: 8000 });
      } else {
        toast.error(formatApiErrorDetail(msg) || "Falha ao enviar.");
      }
    } finally { setWaBusy(false); }
  };

  const closeWaConv = async (conv) => {
    try {
      await api.patch(`/channels/letscall/whatsapp/conversations/${conv.id}`, { estado: "fechada" });
      setWaConvs((prev) => prev.map((c) => c.id === conv.id ? { ...c, estado: "fechada" } : c));
      if (waSel?.id === conv.id) setWaSel((s) => s ? { ...s, estado: "fechada" } : s);
      toast.success("Conversa fechada.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const reopenWaConv = async (conv) => {
    try {
      await api.patch(`/channels/letscall/whatsapp/conversations/${conv.id}`, { estado: "aberta" });
      setWaConvs((prev) => prev.map((c) => c.id === conv.id ? { ...c, estado: "aberta" } : c));
      if (waSel?.id === conv.id) setWaSel((s) => s ? { ...s, estado: "aberta" } : s);
    } catch { /* noop */ }
  };

  useEffect(() => { if (canal === "whatsapp") loadWaConversations(); }, [canal, loadWaConversations]);

  // Auto-refresh WhatsApp a cada 30s
  useEffect(() => {
    if (canal !== "whatsapp") return;
    const t = setInterval(() => loadWaConversations(), 30000);
    return () => clearInterval(t);
  }, [canal, loadWaConversations]);

  // Re-abrir conversa activa quando lista e actualizada
  useEffect(() => {
    if (waSel) {
      const updated = waConvs.find((c) => c.id === waSel.id);
      if (updated) setWaSel(updated);
    }
  }, [waConvs]); // eslint-disable-line

  useEffect(() => {
    if (canal !== "whatsapp") return;
    const handler = (e) => {
      loadWaConversations();
      // Se conversa activa e a que recebeu mensagem, recarregar mensagens
      const convId = e.detail?.conversation_id;
      if (convId && waSel?.id === convId) {
        openWaConversation(waSel);
      }
    };
    window.addEventListener("wa:new-message", handler);
    return () => window.removeEventListener("wa:new-message", handler);
  }, [canal, waSel, loadWaConversations]); // eslint-disable-line

  // --- Associar Conta CRM a Conversa WhatsApp ---
  const [waAssociarConv, setWaAssociarConv] = useState(null);
  const [waAssociarQuery, setWaAssociarQuery] = useState("");
  const [waAssociarResults, setWaAssociarResults] = useState([]);
  const [waAssociarBusy, setWaAssociarBusy] = useState(false);
  // Post-associacao: atualizar contacto CRM
  const [waPostAssociar, setWaPostAssociar] = useState(null); // {phone,accountId,accountNome,convId}
  const [waPostMode, setWaPostMode] = useState(null);          // "novo" | "principal"
  const [waNovoNome, setWaNovoNome] = useState("");
  const [waPostBusy, setWaPostBusy] = useState(false);

  useEffect(() => {
    if (!waAssociarConv || !waAssociarQuery.trim()) { setWaAssociarResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/crm/accounts", { params: { search: waAssociarQuery, limit: 8 } });
        setWaAssociarResults(Array.isArray(data) ? data.slice(0, 8) : []);
      } catch { setWaAssociarResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [waAssociarQuery, waAssociarConv]);

  const associarContaWa = async (acc) => {
    if (!waAssociarConv) return;
    setWaAssociarBusy(true);
    try {
      await api.patch(`/channels/letscall/whatsapp/conversations/${waAssociarConv.id}`, {
        account_id: acc.id,
        contact_name: acc.nome,
      });
      setWaConvs((prev) => prev.map((c) =>
        c.id === waAssociarConv.id ? { ...c, account_id: acc.id, contact_name: acc.nome } : c
      ));
      if (waSel?.id === waAssociarConv.id) {
        setWaSel((s) => s ? { ...s, account_id: acc.id, contact_name: acc.nome } : s);
      }
      toast.success(`Conversa associada a "${acc.nome}".`);
      // Mostrar prompt post-associacao
      setWaPostAssociar({ phone: waAssociarConv.phone, accountId: acc.id, accountNome: acc.nome, convId: waAssociarConv.id });
      setWaPostMode(null);
      setWaNovoNome("");
      setWaAssociarConv(null);
      setWaAssociarQuery("");
      setWaAssociarResults([]);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao associar."); }
    finally { setWaAssociarBusy(false); }
  };

  const guardarNoContactoPrincipal = async () => {
    if (!waPostAssociar) return;
    setWaPostBusy(true);
    try {
      const { data: contactos } = await api.get("/crm/contacts", { params: { account_id: waPostAssociar.accountId } });
      const principal = (contactos || []).find((c) => !c.inativo) || (contactos || [])[0];
      if (!principal) {
        toast.error("Sem contacto principal na conta. Crie um novo contacto.");
        setWaPostMode("novo");
        return;
      }
      await api.put(`/crm/contacts/${principal.id}`, { telemovel: waPostAssociar.phone });
      toast.success(`Numero adicionado ao contacto "${principal.nome}".`);
      setWaPostAssociar(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao atualizar contacto.");
      setWaPostMode(null); // volta ao menu inicial para o utilizador poder escolher outra opcao
    }
    finally { setWaPostBusy(false); }
  };

  const criarNovoContacto = async () => {
    if (!waPostAssociar || !waNovoNome.trim()) return;
    setWaPostBusy(true);
    try {
      await api.post("/crm/contacts", {
        nome: waNovoNome.trim(),
        telemovel: waPostAssociar.phone,
        account_id: waPostAssociar.accountId,
        email: "",
        telefone: "",
        cargo: "",
      });
      toast.success(`Contacto "${waNovoNome.trim()}" criado com sucesso.`);
      setWaPostAssociar(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao criar contacto."); }
    finally { setWaPostBusy(false); }
  };

  // --- Agendadas (tab global) ---
  const [agendadas, setAgendadas] = useState([]);
  const [agendadasLoading, setAgendadasLoading] = useState(false);
  const [agFiltroTipo, setAgFiltroTipo] = useState("");
  const [agFiltroEstado, setAgFiltroEstado] = useState("pendente");
  const [agFiltroSearch, setAgFiltroSearch] = useState("");
  const [reenviarModal, setReenviarModal] = useState(null); // { msg }
  const [reenviarData, setReenviarData] = useState("");
  const [reenviarHora, setReenviarHora] = useState("09:00");
  const [reenviarBusy, setReenviarBusy] = useState(false);

  const loadAgendadas = useCallback(async (tipo, estado) => {
    const t = tipo !== undefined ? tipo : agFiltroTipo;
    const e = estado !== undefined ? estado : agFiltroEstado;
    setAgendadasLoading(true);
    try {
      const params = {};
      if (t) params.tipo = t;
      if (e) params.estado = e;
      const { data } = await api.get("/scheduled-messages", { params });
      setAgendadas(data || []);
    } catch { /* silencioso */ } finally { setAgendadasLoading(false); }
  }, [agFiltroTipo, agFiltroEstado]);

  useEffect(() => { if (canal === "agendadas") loadAgendadas(); }, [canal]); // eslint-disable-line

  const cancelarAgendada = async (m) => {
    if (!confirm("Cancelar esta mensagem agendada?")) return;
    try {
      await api.delete(`/scheduled-messages/${m.id}`);
      toast.success("Mensagem cancelada.");
      loadAgendadas();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao cancelar."); }
  };

  const confirmarReenvio = async () => {
    if (!reenviarModal || !reenviarData) return;
    setReenviarBusy(true);
    const agendado_para = new Date(`${reenviarData}T${reenviarHora || "09:00"}:00`).toISOString();
    try {
      await api.post(`/scheduled-messages/${reenviarModal.id}/resend`, { agendado_para });
      toast.success(`Reagendado para ${new Date(agendado_para).toLocaleString("pt-PT")}.`);
      setReenviarModal(null);
      loadAgendadas();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao reagendar."); }
    finally { setReenviarBusy(false); }
  };

  // filtro de pesquisa por numero/mensagem (client-side)
  const agendadasFiltradas = agendadas.filter((m) => {
    if (!agFiltroSearch.trim()) return true;
    const q = agFiltroSearch.toLowerCase();
    return (m.numero || "").toLowerCase().includes(q) || (m.mensagem || "").toLowerCase().includes(q);
  });

  const loadInbox = async () => {
    try {
      const { data } = await api.get("/chat-ai/inbox", { params: { estado: tab } });
      setRows(data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { loadInbox(); }, [tab]); // eslint-disable-line

  const openSession = async (r) => {
    setSel(r);
    try {
      const { data } = await api.get(`/chat-ai/sessions/${r.id}`);
      setMsgs(data.messages || []);
      setTimeout(() => msgsEnd.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // Deep-link: abre automaticamente a sessao passada via ?session=<sid> (ex: vindo do
  // painel de handoffs ou da timeline CRM). Ajusta a aba pela estado da sessao para
  // que a lista lateral tambem inclua o registo, e limpa o parametro do URL apos abrir.
  useEffect(() => {
    if (!deepSid) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/chat-ai/sessions/${deepSid}`);
        if (cancelled || !data?.session) return;
        const s = data.session;
        setSel(s);
        setMsgs(data.messages || []);
        const nextTab = s.estado === "fechada" ? "fechada" : (s.estado === "aberta" ? "aberta" : "escalada");
        setTab((t) => (t === nextTab || t === "todas" ? t : nextTab));
        if (fromHandoff) toast.message("Conversa aberta a partir do handoff.");
        // limpa o URL para nao reabrir em recargas
        const sp = new URLSearchParams(searchParams);
        sp.delete("session"); sp.delete("from");
        setSearchParams(sp, { replace: true });
        setTimeout(() => msgsEnd.current?.scrollIntoView({ behavior: "smooth" }), 200);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sessao nao encontrada.");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepSid]);

  const reply = async () => {
    if (!texto.trim() || !sel) return;
    setBusy(true);
    try {
      await api.post(`/chat-ai/sessions/${sel.id}/reply`, { texto });
      setTexto("");
      openSession(sel);
      loadInbox();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const closeSession = async () => {
    if (!sel) return;
    if (!confirm("Fechar sessao?")) return;
    try {
      await api.put(`/chat-ai/sessions/${sel.id}/close`);
      toast.success("Sessao fechada."); setSel(null); loadInbox();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const backToAI = async () => {
    if (!sel) return;
    try {
      await api.put(`/chat-ai/sessions/${sel.id}/take-ai`);
      toast.success("Devolvido ao assistente AI."); openSession(sel); loadInbox();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openCfg = async () => {
    try {
      const { data } = await api.get("/chat-ai/config");
      setCfg({ ...data, escalate_keywords_str: (data.escalate_keywords || []).join(", ") });
      setShowCfg(true);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveCfg = async () => {
    try {
      const payload = {
        system_prompt: cfg.system_prompt || "",
        faq: cfg.faq || "",
        model: cfg.model,
        provider: cfg.provider,
        ativo: !!cfg.ativo,
        escalate_keywords: (cfg.escalate_keywords_str || "").split(",").map((x) => x.trim()).filter(Boolean),
      };
      await api.put("/chat-ai/config", payload);
      toast.success("Configuracao guardada."); setShowCfg(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const copyEmbed = async () => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    const code = `<script async src="${backend}/api/public/chat-ai/widget.js"></script>`;
    try { await navigator.clipboard.writeText(code); toast.success("Snippet copiado."); } catch {}
  };

  return (
    <>
      <PageHeader
        overline="Canais"
        title={canal === "email" ? "Inbox Email" : canal === "whatsapp" ? "WhatsApp Inbox" : canal === "agendadas" ? "Mensagens Agendadas" : "Inbox Chat AI"}
        action={
          <div className="flex gap-2">
            {canal === "chat" && (
              <>
                <Btn variant="ghost" onClick={copyEmbed} testid="chat-embed-btn"><Copy size={14} /> Snippet embed</Btn>
                <Btn variant="ghost" onClick={openCfg} testid="chat-cfg-btn"><Settings2 size={14} /> Configurar bot</Btn>
                <Btn variant="ghost" onClick={loadInbox} testid="chat-refresh-btn"><RefreshCw size={14} /> Atualizar</Btn>
              </>
            )}
            {canal === "email" && (
              <Btn variant="ghost" onClick={loadEmail} testid="email-refresh-btn"><RefreshCw size={14} /> Atualizar</Btn>
            )}
            {canal === "whatsapp" && (
              <Btn variant="ghost" onClick={loadWaConversations} testid="wa-refresh-btn"><RefreshCw size={14} /> Atualizar</Btn>
            )}
            {canal === "agendadas" && (
              <Btn variant="ghost" onClick={() => loadAgendadas()} testid="ag-refresh-btn"><RefreshCw size={14} /> Atualizar</Btn>
            )}
          </div>
        }
      />

      {/* Selector de canal */}
      <div className="flex gap-1 mb-3 border-b border-tint" data-testid="canal-tabs">
        {CANAL_TABS.map((c) => {
          const I = c.icon;
          const isWa = c.value === "whatsapp";
          const unread = isWa ? waConvs.filter((w) => w.unread_count > 0 && w.estado === "aberta").length : 0;
          return (
            <button key={c.value} onClick={() => setCanal(c.value)} data-testid={`canal-tab-${c.value}`}
              className={`inline-flex items-center gap-1.5 text-sm px-4 py-2 border-b-2 -mb-px ${canal === c.value ? "border-blue text-navy font-semibold" : "border-transparent text-slate-500 hover:text-navy"}`}>
              <I size={14} className={isWa ? "text-[#25d366]" : ""} />
              {c.label}
              {unread > 0 && (
                <span className="ml-0.5 text-[9px] font-bold bg-[#25d366] text-white rounded-full px-1.5 py-0.5 min-w-[18px] text-center"
                  data-testid="wa-unread-badge">{unread}</span>
              )}
            </button>
          );
        })}
      </div>

      {canal === "chat" && (
        <div className="flex gap-2 mb-3" data-testid="chat-tabs">
          {ESTADO_TABS.map((t) => (
            <button key={t.value} onClick={() => setTab(t.value)} data-testid={`chat-tab-${t.value}`}
              className={`text-xs px-3 py-1.5 rounded ${tab === t.value ? "bg-navy text-white" : "border border-tint text-slate-600 hover:bg-slate-50"}`}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      {canal === "chat" && (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Lista */}
        <div className="md:col-span-1 space-y-1.5 max-h-[70vh] overflow-y-auto" data-testid="chat-list">
          {rows.length === 0 && <div className="text-center text-muted2 text-xs py-6">Sem sessoes.</div>}
          {rows.map((r) => (
            <button key={r.id} onClick={() => openSession(r)} data-testid={`chat-row-${r.id}`}
              className={`w-full text-left border rounded-md p-2.5 transition ${sel?.id === r.id ? "border-blue bg-blue/5" : "border-tint bg-white hover:border-blue"}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-navy truncate">{r.nome || "Visitante"}</div>
                  <div className="text-[10px] text-slate-500 truncate">{r.email || r.telefone || "sem contacto"}</div>
                </div>
                <div className="flex flex-col items-end gap-0.5 shrink-0">
                  <Badge value={r.estado} />
                  {r.handoff_pendente && (
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200 uppercase" data-testid={`chat-row-handoff-${r.id}`}>Handoff</span>
                  )}
                </div>
              </div>
              <div className="text-[11px] text-slate-500 mt-1 truncate">
                {r.ultima?.de === "cliente" ? <><User2 size={9} className="inline"/> </> : <><Bot size={9} className="inline"/> </>}
                {r.ultima?.texto || "..."}
              </div>
              <div className="flex items-center justify-between mt-1 text-[10px] text-slate-400">
                <span>{r.n_mensagens || 0} msg</span>
                <span>{r.modo}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Detalhe */}
        <div className="md:col-span-2 border border-tint rounded-lg bg-white flex flex-col min-h-[70vh]" data-testid="chat-detail">
          {!sel && <div className="flex-1 flex items-center justify-center text-muted2 text-sm">Selecione uma sessao.</div>}
          {sel && (
            <>
              <div className="flex items-center justify-between px-3 py-2 border-b border-tint bg-slate-50">
                <div>
                  <div className="text-sm font-semibold text-navy">{sel.nome}</div>
                  <div className="text-[11px] text-slate-500">{sel.email || sel.telefone || "-"} - {sel.origem_url ? new URL(sel.origem_url).host : "-"}</div>
                </div>
                <div className="flex gap-1">
                  <Badge value={sel.estado} />
                  <button onClick={backToAI} title="Voltar ao AI" data-testid="chat-back-ai" className="text-[11px] px-2 py-1 rounded border border-tint hover:bg-slate-100 inline-flex items-center gap-1"><Bot size={11} /> AI</button>
                  <button onClick={closeSession} title="Fechar" data-testid="chat-close" className="text-[11px] px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50 inline-flex items-center gap-1"><X size={11} /></button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50" data-testid="chat-messages">
                {msgs.map((m) => {
                  if (m.de === "sistema") {
                    return <div key={m.id} className="text-center text-[10px] text-slate-400 italic">{m.texto}</div>;
                  }
                  const mine = m.de === "agente" || m.de === "bot";
                  return (
                    <div key={m.id} className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${mine ? "ml-auto bg-blue text-white" : "bg-white border border-tint"}`}>
                      <div className="text-[9px] uppercase opacity-70 mb-0.5 flex items-center gap-1">
                        {m.de === "cliente" ? <User2 size={9} /> : m.de === "bot" ? <Bot size={9} /> : <MessageSquare size={9} />}
                        {m.de === "agente" ? (m.agente_email || "agente") : m.de}
                      </div>
                      <div className="whitespace-pre-wrap">{m.texto}</div>
                    </div>
                  );
                })}
                <div ref={msgsEnd} />
              </div>
              <div className="p-2 border-t border-tint flex gap-2">
                <input
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && reply()}
                  placeholder="Escreva a resposta..."
                  data-testid="chat-input"
                  className="flex-1 px-3 py-2 text-sm border border-tint rounded-md focus:outline-none focus:ring-2 focus:ring-blue/20"
                />
                <Btn onClick={reply} disabled={busy || !texto.trim()} testid="chat-send"><Send size={14} /></Btn>
              </div>
            </>
          )}
        </div>
      </div>
      )}

      {canal === "email" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="email-inbox">
          <div className="md:col-span-1 space-y-1.5 max-h-[70vh] overflow-y-auto" data-testid="email-list">
            {emailThreads.length === 0 && <div className="text-center text-muted2 text-xs py-6">Sem threads de email.</div>}
            {emailThreads.map((t) => (
              <button key={t.email} onClick={() => openEmailThread(t)} data-testid={`email-row-${t.email}`}
                className={`w-full text-left border rounded-md p-2.5 transition ${emailSel?.email === t.email ? "border-blue bg-blue/5" : "border-tint bg-white hover:border-blue"}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-navy truncate">{t.nome || t.email}</div>
                    <div className="text-[10px] text-slate-500 truncate">{t.email}</div>
                  </div>
                  <span className="text-[10px] text-slate-500 shrink-0">{t.n} msg</span>
                </div>
                <div className="text-[11px] text-slate-700 mt-1 truncate font-medium">{t.ultimo_assunto || "(sem assunto)"}</div>
                <div className="text-[11px] text-slate-500 truncate">{t.ultimo_direcao === "in" ? "<< " : ">> "}{t.ultimo_texto || "..."}</div>
              </button>
            ))}
          </div>
          <div className="md:col-span-2 border border-tint rounded-lg bg-white flex flex-col min-h-[70vh]" data-testid="email-detail">
            {!emailSel && <div className="flex-1 flex items-center justify-center text-muted2 text-sm">Selecione uma thread.</div>}
            {emailSel && (
              <>
                <div className="flex items-center justify-between px-3 py-2 border-b border-tint bg-slate-50">
                  <div>
                    <div className="text-sm font-semibold text-navy">{emailSel.nome || "Contacto"}</div>
                    <div className="text-[11px] text-slate-500">{emailSel.email}</div>
                  </div>
                  {emailSel.account_id && (
                    <a href={`/app/crm/contas/${emailSel.account_id}`} className="text-[11px] font-semibold text-blue hover:underline" data-testid="email-account-link">Abrir ficha</a>
                  )}
                </div>
                <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50" data-testid="email-messages">
                  {emailMsgs.length === 0 && <div className="text-center text-muted2 text-xs py-6">Sem mensagens.</div>}
                  {emailMsgs.map((m, i) => {
                    const mine = m.direcao === "out";
                    return (
                      <div key={i} className={`rounded-lg px-3 py-2 text-sm border ${mine ? "ml-auto max-w-[85%] bg-blue text-white border-blue" : "max-w-[85%] bg-white border-tint"}`}>
                        <div className="text-[9px] uppercase opacity-70 mb-0.5 flex items-center justify-between gap-2">
                          <span>{mine ? "Enviado" : "Recebido"} {m.estado ? `- ${m.estado}` : ""}</span>
                          <span>{m.em ? new Date(m.em).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" }) : ""}</span>
                        </div>
                        <div className="text-[13px] font-semibold mb-1 truncate">{m.assunto || "(sem assunto)"}</div>
                        <div className={`text-[12px] whitespace-pre-wrap ${mine ? "text-white/90" : "text-slate-700"}`}>{m.corpo}</div>
                      </div>
                    );
                  })}
                </div>
                <div className="px-3 py-2 border-t border-tint bg-slate-50 text-[11px] text-muted2">
                  Envio de email fora do inbox esta em /app/emails (composicao rica com templates e anexos).
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {canal === "whatsapp" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="wa-inbox">
          {/* Filtro estado + lista conversas */}
          <div className="md:col-span-1 flex flex-col gap-2">
            <div className="flex gap-1.5">
              {[{ v: "", l: "Todas" }, { v: "aberta", l: "Abertas" }, { v: "fechada", l: "Fechadas" }].map((o) => (
                <button key={o.v} onClick={() => { setWaEstado(o.v); setWaSel(null); }}
                  data-testid={`wa-estado-${o.v || "todas"}`}
                  className={`text-xs px-2.5 py-1 rounded border transition ${waEstado === o.v ? "bg-[#25d366] text-white border-[#25d366]" : "border-tint text-slate-600 hover:bg-slate-50"}`}>
                  {o.l}
                  {o.v === "aberta" && waConvs.filter((c) => c.estado === "aberta").length > 0 && (
                    <span className="ml-1 font-bold">{waConvs.filter((c) => c.estado === "aberta").length}</span>
                  )}
                </button>
              ))}
            </div>
            <div className="space-y-1.5 max-h-[70vh] overflow-y-auto" data-testid="wa-list">
              {waConvs.length === 0 && (
                <div className="text-center text-muted2 text-xs py-8 border border-tint rounded-lg bg-white">
                  Sem conversas WhatsApp.{" "}
                  <span className="block mt-1 opacity-70">As mensagens recebidas aparecem automaticamente via webhook LetsCall.</span>
                </div>
              )}
              {waConvs.map((conv) => (
                <button key={conv.id} onClick={() => openWaConversation(conv)}
                  data-testid={`wa-conv-${conv.id}`}
                  className={`w-full text-left border rounded-lg p-2.5 transition ${waSel?.id === conv.id ? "border-[#25d366] bg-[#25d366]/5" : "border-tint bg-white hover:border-[#25d366]/50"}`}>
                  <div className="flex items-start justify-between gap-1">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <div className={`w-2 h-2 rounded-full shrink-0 ${conv.estado === "aberta" ? "bg-[#25d366]" : "bg-slate-300"}`} />
                        <span className="text-sm font-semibold text-navy truncate">
                          {conv.contact_name || conv.phone}
                        </span>
                      </div>
                      <div className="text-[10px] text-slate-500 font-mono ml-3.5">{conv.phone}</div>
                    </div>
                    <div className="shrink-0 flex flex-col items-end gap-0.5">
                      {conv.unread_count > 0 && (
                        <span className="text-[9px] font-bold bg-[#25d366] text-white rounded-full px-1.5 py-0.5 min-w-[18px] text-center"
                          data-testid={`wa-unread-${conv.id}`}>{conv.unread_count}</span>
                      )}
                      <span className="text-[10px] text-slate-400">{dt(conv.last_message_at)}</span>
                    </div>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1 truncate ml-3.5">{conv.last_message_body || "..."}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Thread de mensagens */}
          <div className="md:col-span-2 border border-tint rounded-lg bg-white flex flex-col min-h-[70vh]" data-testid="wa-detail">
            {!waSel ? (
              <div className="flex-1 flex flex-col items-center justify-center text-muted2 text-sm gap-2">
                <MessageCircle size={32} className="text-[#25d366]/40" />
                Selecione uma conversa.
              </div>
            ) : (
              <>
                {/* Header conversa */}
                <div className="flex items-center justify-between px-3 py-2 border-b border-tint bg-slate-50">
                  <div>
                    <div className="flex items-center gap-2">
                      <MessageCircle size={14} className="text-[#25d366]" />
                      <span className="text-sm font-semibold text-navy">{waSel.contact_name || waSel.phone}</span>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${waSel.estado === "aberta" ? "bg-green-50 text-green-700 border-green-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
                        {waSel.estado}
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-500 ml-5.5 font-mono">{waSel.phone}</div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap justify-end">
                    {/* Botao Associar/Abrir ficha */}
                    {waSel.account_id ? (
                      <a href={`/app/crm/contas/${waSel.account_id}`}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue hover:underline"
                        data-testid="wa-account-link">
                        <Link2 size={11} /> {waSel.contact_name || "Abrir ficha"}
                      </a>
                    ) : (
                      <button onClick={() => { setWaAssociarConv(waSel); setWaAssociarQuery(""); setWaAssociarResults([]); }}
                        data-testid="wa-associar-btn"
                        className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-blue/30 text-blue hover:bg-blue/5 font-semibold">
                        <Link2 size={11} /> Associar a conta
                      </button>
                    )}
                    {waSel.estado === "aberta" ? (
                      <button onClick={() => closeWaConv(waSel)} data-testid="wa-close-btn"
                        className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-slate-200 text-slate-500 hover:bg-slate-100">
                        <Lock size={11} /> Fechar
                      </button>
                    ) : (
                      <button onClick={() => reopenWaConv(waSel)} data-testid="wa-reopen-btn"
                        className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-[#25d366]/30 text-[#25d366] hover:bg-[#25d366]/5">
                        <MessageCircle size={11} /> Reabrir
                      </button>
                    )}
                  </div>
                </div>

                {/* Mensagens */}
                <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-[#ece5dd]/40" data-testid="wa-messages">
                  {waMsgs.length === 0 && (
                    <div className="text-center text-muted2 text-xs py-6">Sem mensagens.</div>
                  )}
                  {waMsgs.map((m, i) => {
                    const isOut = m.direction === "outbound";
                    return (
                      <div key={m.id || i} className={`flex ${isOut ? "justify-end" : "justify-start"}`}
                        data-testid={`wa-msg-${i}`}>
                        <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm shadow-sm ${isOut ? "bg-[#d9fdd3] text-navy" : "bg-white border border-tint text-navy"}`}>
                          {!isOut && (
                            <div className="text-[9px] font-semibold text-[#25d366] mb-0.5 uppercase tracking-wide">
                              {m.sender_name || "Cliente"}
                            </div>
                          )}
                          <div className="whitespace-pre-wrap text-[13px]">{m.body}</div>
                          <div className={`text-[9px] mt-1 flex items-center gap-0.5 ${isOut ? "text-slate-500 justify-end" : "text-slate-400"}`}>
                            {dt(m.em)}
                            {isOut && <CheckCheck size={11} className="text-[#25d366]" />}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={waMsgsEnd} />
                </div>

                {/* Input resposta */}
                {waSel.estado === "fechada" ? (
                  <div className="px-3 py-2.5 border-t border-tint bg-slate-50 text-[11.5px] text-amber-700 flex items-center gap-1.5">
                    <Lock size={12} /> Conversa fechada. Reabra para responder.
                  </div>
                ) : (
                  <div className="p-2 border-t border-tint flex gap-2 bg-[#f0f2f5]" data-testid="wa-reply-bar">
                    <input
                      value={waTexto}
                      onChange={(e) => setWaTexto(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendWaReply()}
                      placeholder="Escreva a resposta... (Enter para enviar)"
                      data-testid="wa-reply-input"
                      className="flex-1 px-3 py-2 text-sm border border-tint rounded-full focus:outline-none focus:ring-2 focus:ring-[#25d366]/30 bg-white"
                    />
                    <Btn onClick={sendWaReply} disabled={waBusy || !waTexto.trim()} testid="wa-send-btn"
                      className="bg-[#25d366] hover:bg-[#1da355] text-white rounded-full !px-3">
                      <Send size={15} />
                    </Btn>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ---- Tab Agendadas ---- */}
      {canal === "agendadas" && (
        <div data-testid="agendadas-inbox">
          {/* Filtros */}
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div>
              <label className="block text-[11px] font-semibold text-navy mb-1 uppercase">Tipo</label>
              <select value={agFiltroTipo}
                onChange={(e) => { const v = e.target.value; setAgFiltroTipo(v); loadAgendadas(v, agFiltroEstado); }}
                data-testid="ag-filtro-tipo"
                className="px-2.5 py-1.5 border border-tint rounded-lg text-sm bg-white min-w-[130px]">
                <option value="">Todos os tipos</option>
                <option value="sms">SMS</option>
                <option value="whatsapp">WhatsApp</option>
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-navy mb-1 uppercase">Estado</label>
              <select value={agFiltroEstado}
                onChange={(e) => { const v = e.target.value; setAgFiltroEstado(v); loadAgendadas(agFiltroTipo, v); }}
                data-testid="ag-filtro-estado"
                className="px-2.5 py-1.5 border border-tint rounded-lg text-sm bg-white min-w-[150px]">
                <option value="">Todos os estados</option>
                <option value="pendente">Pendente</option>
                <option value="enviado">Enviado</option>
                <option value="falhado">Falhado</option>
                <option value="cancelado">Cancelado</option>
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-navy mb-1 uppercase">Pesquisar</label>
              <div className="relative">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input value={agFiltroSearch} onChange={(e) => setAgFiltroSearch(e.target.value)}
                  placeholder="Numero ou mensagem..."
                  data-testid="ag-filtro-search"
                  className="pl-8 pr-3 py-1.5 border border-tint rounded-lg text-sm bg-white w-52" />
              </div>
            </div>
            <div className="ml-auto text-xs text-muted2 self-end pb-1.5" data-testid="ag-total-label">
              {agendadasFiltradas.length} / {agendadas.length} mensagem{agendadas.length !== 1 ? "s" : ""}
            </div>
          </div>

          {agendadasLoading ? (
            <div className="space-y-2">{[1, 2, 3, 4].map((n) => <div key={n} className="h-16 bg-slate-100 rounded-lg animate-pulse" />)}</div>
          ) : agendadasFiltradas.length === 0 ? (
            <div className="text-sm text-muted2 py-10 text-center border border-tint rounded-lg bg-white" data-testid="ag-empty">
              Sem mensagens agendadas para os filtros selecionados.
            </div>
          ) : (
            <div className="space-y-2">
              {agendadasFiltradas.map((m, i) => {
                const isSms = m.tipo === "sms";
                const estadoCor = {
                  pendente: "bg-amber-50 text-amber-700 border-amber-200",
                  enviado: "bg-green-50 text-green-700 border-green-200",
                  falhado: "bg-red-50 text-red-600 border-red-200",
                  cancelado: "bg-slate-50 text-slate-500 border-slate-200",
                }[m.estado] || "bg-slate-50 text-slate-500 border-slate-200";
                const dtShort = (s) => s ? new Date(s).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" }) : "-";
                return (
                  <div key={m.id || i} className="flex items-start gap-3 border border-tint rounded-lg p-3 bg-white hover:border-slate-300 transition"
                    data-testid={`ag-item-${i}`}>
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${isSms ? "bg-blue/10" : "bg-[#25d366]/10"}`}>
                      {isSms
                        ? <MessageSquare size={16} className="text-blue" />
                        : <MessageCircle size={16} className="text-[#25d366]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-navy font-mono">{m.numero}</span>
                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border ${estadoCor}`}
                          data-testid={`ag-estado-${i}`}>{m.estado}</span>
                        <span className="text-[10px] text-slate-400 uppercase font-semibold">{m.tipo}</span>
                        {m.account_id && (
                          <a href={`/app/crm/contas/${m.account_id}`} className="text-[10px] text-blue hover:underline">ver ficha</a>
                        )}
                      </div>
                      <div className="text-[13px] text-slate-700 mt-0.5 truncate">
                        {m.template_name ? <span className="italic text-slate-500">Template: {m.template_name}</span> : m.mensagem}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-[10px] text-slate-400">
                        <span className="flex items-center gap-0.5"><Calendar size={9} /> Agendado: {dtShort(m.agendado_para)}</span>
                        {m.enviado_em && <span>Enviado: {dtShort(m.enviado_em)}</span>}
                        {m.criado_por_email && <span>Por: {m.criado_por_email}</span>}
                        {m.erro && <span className="text-red-500 truncate max-w-[250px]" title={m.erro}>Erro: {m.erro}</span>}
                        {m.original_id && <span className="text-blue">reagendado</span>}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5 shrink-0">
                      {m.estado === "pendente" && (
                        <button onClick={() => cancelarAgendada(m)} data-testid={`ag-cancelar-${i}`}
                          className="text-[11px] px-2 py-1 border border-red-200 text-red-600 rounded hover:bg-red-50 transition">
                          Cancelar
                        </button>
                      )}
                      {(m.estado === "falhado" || m.estado === "cancelado") && (
                        <button
                          onClick={() => { setReenviarModal(m); setReenviarData(""); setReenviarHora("09:00"); }}
                          data-testid={`ag-reenviar-${i}`}
                          className="text-[11px] px-2 py-1 border border-blue/30 text-blue rounded hover:bg-blue/5 transition inline-flex items-center gap-1">
                          <RotateCcw size={10} /> Reenviar
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Modal: Reagendar mensagem */}
      {reenviarModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setReenviarModal(null)} data-testid="reenviar-modal">
          <div className="bg-white rounded-xl w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-tint">
              <h3 className="text-sm font-semibold text-navy flex items-center gap-2">
                <RotateCcw size={14} className="text-blue" /> Reagendar mensagem
              </h3>
              <button onClick={() => setReenviarModal(null)} className="text-slate-400 hover:text-navy"><X size={15} /></button>
            </div>
            <div className="p-4 space-y-3">
              <div className="bg-slate-50 rounded-lg p-2.5 text-[12px] text-slate-600 space-y-0.5">
                <div className="flex items-center gap-1.5">
                  {reenviarModal.tipo === "sms" ? <MessageSquare size={12} className="text-blue" /> : <MessageCircle size={12} className="text-[#25d366]" />}
                  <span className="font-mono font-semibold">{reenviarModal.numero}</span>
                  <span className="text-slate-400 uppercase text-[10px]">{reenviarModal.tipo}</span>
                </div>
                <div className="text-slate-500 truncate">{reenviarModal.template_name ? `Template: ${reenviarModal.template_name}` : reenviarModal.mensagem}</div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] font-semibold text-navy mb-1 uppercase">Nova data</label>
                  <input type="date" value={reenviarData} onChange={(e) => setReenviarData(e.target.value)}
                    min={new Date().toISOString().split("T")[0]}
                    data-testid="reenviar-data"
                    className="w-full px-2.5 py-1.5 text-sm border border-tint rounded" />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-navy mb-1 uppercase">Hora</label>
                  <input type="time" value={reenviarHora} onChange={(e) => setReenviarHora(e.target.value)}
                    data-testid="reenviar-hora"
                    className="w-full px-2.5 py-1.5 text-sm border border-tint rounded" />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-tint bg-slate-50">
              <Btn variant="ghost" onClick={() => setReenviarModal(null)}>Cancelar</Btn>
              <Btn onClick={confirmarReenvio} disabled={!reenviarData || reenviarBusy} testid="reenviar-confirmar">
                <RotateCcw size={13} /> {reenviarBusy ? "A reagendar..." : "Confirmar reagendamento"}
              </Btn>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Apos associar - guardar numero no CRM */}
      {waPostAssociar && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          data-testid="wa-post-associar-modal">
          <div className="bg-white rounded-xl w-full max-w-sm shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-tint">
              <h3 className="text-sm font-semibold text-navy flex items-center gap-2">
                <User2 size={14} className="text-blue" /> Guardar numero no CRM?
              </h3>
              <p className="text-[11.5px] text-slate-500 mt-0.5">
                <span className="font-mono font-semibold text-navy">{waPostAssociar.phone}</span> associado a <span className="font-semibold text-navy">{waPostAssociar.accountNome}</span>
              </p>
            </div>
            <div className="p-4">
              {!waPostMode ? (
                <div className="space-y-2">
                  <button onClick={() => { setWaPostMode("principal"); guardarNoContactoPrincipal(); }}
                    disabled={waPostBusy}
                    data-testid="wa-post-contacto-principal"
                    className="w-full text-left px-3 py-2.5 border border-tint rounded-lg hover:border-blue/40 hover:bg-blue/5 transition">
                    <div className="text-sm font-semibold text-navy">Adicionar ao contacto principal</div>
                    <div className="text-[11px] text-slate-500">Adiciona o numero ao telemovel do contacto existente</div>
                  </button>
                  <button onClick={() => setWaPostMode("novo")} disabled={waPostBusy}
                    data-testid="wa-post-novo-contacto"
                    className="w-full text-left px-3 py-2.5 border border-tint rounded-lg hover:border-blue/40 hover:bg-blue/5 transition">
                    <div className="text-sm font-semibold text-navy">Criar novo contacto</div>
                    <div className="text-[11px] text-slate-500">Cria um novo contacto na conta com este numero</div>
                  </button>
                  <button onClick={() => setWaPostAssociar(null)} data-testid="wa-post-mais-tarde"
                    className="w-full text-center text-[12px] text-slate-400 hover:text-slate-600 py-1.5">
                    Mais tarde
                  </button>
                </div>
              ) : waPostMode === "novo" ? (
                <div className="space-y-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-navy mb-1 uppercase">Nome do contacto</label>
                    <input autoFocus value={waNovoNome} onChange={(e) => setWaNovoNome(e.target.value)}
                      placeholder="Ex: Ana Silva"
                      data-testid="wa-novo-contacto-nome"
                      className="w-full px-3 py-2 text-sm border border-tint rounded-lg focus:outline-none focus:ring-2 focus:ring-blue/20" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-navy mb-1 uppercase">Telemovel (WhatsApp)</label>
                    <input value={waPostAssociar.phone} readOnly
                      className="w-full px-3 py-2 text-sm border border-tint rounded-lg bg-slate-50 font-mono text-slate-600" />
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Btn variant="ghost" onClick={() => setWaPostMode(null)} className="flex-1">Voltar</Btn>
                    <Btn onClick={criarNovoContacto} disabled={!waNovoNome.trim() || waPostBusy}
                      testid="wa-novo-contacto-confirmar" className="flex-1">
                      {waPostBusy ? "A criar..." : "Criar contacto"}
                    </Btn>
                  </div>
                </div>
              ) : waPostMode === "principal" ? (
                <div className="flex flex-col items-center justify-center py-6 gap-3">
                  <div className="w-8 h-8 border-2 border-blue border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm text-slate-500">A guardar numero no contacto...</p>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Modal: Associar conta CRM a conversa WhatsApp */}
      {waAssociarConv && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setWaAssociarConv(null)} data-testid="wa-associar-modal">
          <div className="bg-white rounded-xl w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-tint">
              <h3 className="text-sm font-semibold text-navy flex items-center gap-2">
                <Link2 size={14} className="text-blue" /> Associar a conta CRM
              </h3>
              <button onClick={() => setWaAssociarConv(null)} className="text-slate-400 hover:text-navy"><X size={15} /></button>
            </div>
            <div className="p-4 space-y-3">
              <div className="text-[12px] text-slate-500">
                Numero: <span className="font-mono font-semibold text-navy">{waAssociarConv.phone}</span>
              </div>
              <div className="relative">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  autoFocus
                  value={waAssociarQuery}
                  onChange={(e) => setWaAssociarQuery(e.target.value)}
                  placeholder="Pesquisar conta por nome ou NIF..."
                  data-testid="wa-associar-search"
                  className="w-full pl-9 pr-3 py-2.5 text-sm border border-tint rounded-lg focus:outline-none focus:ring-2 focus:ring-blue/20"
                />
              </div>
              {waAssociarQuery.trim() && waAssociarResults.length === 0 && (
                <div className="text-xs text-muted2 py-2 text-center">Sem resultados para "{waAssociarQuery}"</div>
              )}
              <div className="space-y-1 max-h-52 overflow-y-auto" data-testid="wa-associar-results">
                {waAssociarResults.map((acc) => (
                  <button key={acc.id} onClick={() => associarContaWa(acc)} disabled={waAssociarBusy}
                    data-testid={`wa-associar-result-${acc.id}`}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-blue/5 border border-transparent hover:border-blue/20 transition">
                    <div className="text-sm font-semibold text-navy">{acc.nome}</div>
                    <div className="text-[11px] text-slate-500 flex gap-2">
                      {acc.nif && <span>NIF: {acc.nif}</span>}
                      {acc.email && <span>{acc.email}</span>}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal open={showCfg} onClose={() => setShowCfg(false)} title="Configuracao do Chat AI" testid="chat-cfg-modal" size="lg">
        {cfg && (
          <>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!cfg.ativo} onChange={(e) => setCfg({ ...cfg, ativo: e.target.checked })} data-testid="cfg-ativo" />
              Chat AI ativo (se desligado, o widget continua a funcionar mas sem AI - vai direto para agentes)
            </label>
            <div className="grid grid-cols-2 gap-3">
              <Selecter label="Provider" value={cfg.provider} onChange={(v) => setCfg({ ...cfg, provider: v })} options={PROVIDERS} testid="cfg-provider" />
              <Field label="Model" value={cfg.model} onChange={(v) => setCfg({ ...cfg, model: v })} testid="cfg-model" />
            </div>
            <TextArea label="System prompt (persona do bot)" value={cfg.system_prompt} onChange={(v) => setCfg({ ...cfg, system_prompt: v })} testid="cfg-sys" />
            <TextArea label="FAQ / conhecimento" value={cfg.faq} onChange={(v) => setCfg({ ...cfg, faq: v })} testid="cfg-faq" />
            <Field label="Escalate keywords (separadas por virgula)" value={cfg.escalate_keywords_str} onChange={(v) => setCfg({ ...cfg, escalate_keywords_str: v })} testid="cfg-kws" />
            <div className="flex justify-end gap-2 pt-2">
              <Btn variant="ghost" onClick={() => setShowCfg(false)}>Cancelar</Btn>
              <Btn onClick={saveCfg} testid="cfg-save">Guardar</Btn>
            </div>
          </>
        )}
      </Modal>
    </>
  );
}
